import { chartInk, chartPalette } from '../../design-system/tokens';
import './BoxPlot.css';

export interface BoxDatum {
  label: string;
  lowest: number;
  low: number;
  middle: number;
  high: number;
  highest: number;
}

/**
 * A box plot, drawn by hand.
 *
 * Plotly's basic bundle carries scatter, bar and pie only, and pulling in the
 * full build for two chart types would triple what every dashboard downloads.
 * Five numbers per group is a rectangle and three lines — drawing it here keeps
 * the bundle small and every colour a token.
 */
export function BoxPlot({ data, valueLabel }: { data: BoxDatum[]; valueLabel: string }) {
  if (!data.length) return <p className="sc-box__empty">No rows matched.</p>;

  const lowest = Math.min(...data.map((entry) => entry.lowest));
  const highest = Math.max(...data.map((entry) => entry.highest));
  const span = highest - lowest || 1;

  const height = 220;
  const top = 12;
  const bottom = 28;
  const usable = height - top - bottom;
  const slot = 100 / data.length;
  const boxWidth = Math.min(slot * 0.5, 12);

  const y = (value: number) => top + usable - ((value - lowest) / span) * usable;

  const readable = (value: number) =>
    Math.abs(value) >= 1000
      ? value.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <figure className="sc-box">
      {/* A box without a scale is a shape. These two numbers make it a measure. */}
      <div className="sc-box__scale">
        <span>{readable(highest)}</span>
        <span>{readable(lowest)}</span>
      </div>
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="sc-box__plot"
        role="img"
        aria-label={`Box plot of ${valueLabel} by group. The table below carries the same numbers.`}
      >
        {data.map((entry, index) => {
          const centre = slot * index + slot / 2;
          const colour = chartPalette[index % chartPalette.length];
          return (
            <g key={entry.label}>
              {/* The whole range, lowest to highest. */}
              <line
                x1={centre}
                x2={centre}
                y1={y(entry.highest)}
                y2={y(entry.lowest)}
                stroke={chartInk.axis}
                strokeWidth={0.4}
                vectorEffect="non-scaling-stroke"
              />
              {/* The middle half. */}
              <rect
                x={centre - boxWidth / 2}
                width={boxWidth}
                y={y(entry.high)}
                height={Math.max(y(entry.low) - y(entry.high), 1)}
                fill={colour}
                fillOpacity={0.25}
                stroke={colour}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                rx={1}
              />
              {/* The median. */}
              <line
                x1={centre - boxWidth / 2}
                x2={centre + boxWidth / 2}
                y1={y(entry.middle)}
                y2={y(entry.middle)}
                stroke={colour}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}
      </svg>
      <div className="sc-box__labels">
        {data.map((entry) => (
          <span key={entry.label} className="sc-box__label">
            {entry.label}
          </span>
        ))}
      </div>
    </figure>
  );
}
